import { useEffect, useRef, useCallback } from "react";

const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
] as const;

/**
 * Logs the user out after TIMEOUT_MS of inactivity.
 * Activity events reset the timer. Only active for authenticated users
 * (caller is responsible for gating).
 */
export function useSessionTimeout(logout: () => void) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutRef = useRef(logout);

  useEffect(() => {
    logoutRef.current = logout;
  });

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => logoutRef.current(), TIMEOUT_MS);
  }, []);

  useEffect(() => {
    resetTimer();

    const handleActivity = () => resetTimer();
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true }),
    );

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
    };
  }, [resetTimer]);
}
